import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiMapPin, FiPlus, FiMinus, FiExternalLink } = FiIcons;

const Instructors = () => {
  const [expandedInstructor, setExpandedInstructor] = useState(null);

  const instructors = [
    {
      name: 'Olu',
      experience: '30+ years driving experience',
      specialization: 'Manual & Automatic',
      areas: 'Ashford, Isleworth, Chertsey, Addlestone, Weybridge, Walton-On-Thames, NewHaw, Virginia Water',
      image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752375676209-Olu.jpg',
      aboutMe: 'With over 30 years of driving experience and a previous career in social care, I bring patience, clear communication, and a calm approach to teaching. I\'m a fully qualified instructor based in Ashford, covering Isleworth and Chertsey test areas. I teach both manual and automatic cars and focus on making lessons safe, enjoyable and tailored to each learner\'s pace. I enjoy meeting new students and helping them gain the confidence to pass their test.',
      linkBio: 'https://linke.to/olu'
    },
    {
      name: 'Wura',
      experience: 'Teaching background',
      specialization: 'Automatic',
      areas: 'Isleworth, Chertsey, Addlestone, Weybridge, Walton-On-Thames, NewHaw, Virginia Water',
      image: 'https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1752375625734-Wura.jpg',
      aboutMe: 'Friendly, patient and professional instructor with a background in teaching and experience across diverse roles. I enjoy working with learners who are optimistic, open to new ideas and ready to build confidence behind the wheel. I primarily teach automatic lessons across Isleworth and Chertsey test areas, with occasional manual lessons by arrangement. I focus on practical, supportive coaching to help you develop safe, independent driving skills. Ready to get started? I\'m happy to support you on your driving journey.',
      linkBio: 'https://linke.to/wura'
    }
  ];

  const toggleExpanded = (index) => {
    setExpandedInstructor(expandedInstructor === index ? null : index);
  };

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4 transition-colors">
            Meet Your Instructors
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto transition-colors">
            Our instructors adapt lessons to your pace and learning style for a personalised experience.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {instructors.map((instructor, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-700 flex flex-col h-full"
            >
              <div className="flex items-center gap-6 mb-6">
                <img
                  src={instructor.image}
                  alt={`${instructor.name} - Driving Instructor`}
                  className="w-20 h-20 rounded-full object-cover border-4 border-blue-100 dark:border-blue-900"
                />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1 transition-colors">
                    {instructor.name}
                  </h3>
                  <p className="text-blue-600 dark:text-blue-400 font-semibold transition-colors">
                    {instructor.experience}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6 flex-grow">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="text-gray-700 dark:text-gray-300 transition-colors">
                    {instructor.specialization}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiMapPin} className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <span className="text-gray-700 dark:text-gray-300 transition-colors">
                    {instructor.areas}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <button
                  onClick={() => toggleExpanded(index)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="font-semibold text-gray-900 dark:text-white transition-colors">
                    About Me
                  </span>
                  <SafeIcon
                    icon={expandedInstructor === index ? FiMinus : FiPlus}
                    className="w-5 h-5 text-blue-600 dark:text-blue-400 transition-colors"
                  />
                </button>
                {expandedInstructor === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
                  >
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed transition-colors">
                      {instructor.aboutMe}
                    </p>
                  </motion.div>
                )}
              </div>

              <a
                href={instructor.linkBio}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all duration-200 font-semibold mt-auto"
              >
                <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
                <span>Contact {instructor.name}</span>
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Instructors;